/**
 * SignContractPage - Public page for clients to sign Promise to Pay contracts
 * - Shows lender signature for promissory notes
 * - Checkboxes to acknowledge terms
 * - ID upload and borrower signature
 * - Sends copy to both parties
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, Upload, Check, AlertCircle, Pen, CheckSquare, Square, Type, PenTool } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Cursive signature font styles
const SIGNATURE_STYLES = [
  { id: 'style1', name: 'Elegant', fontFamily: "'Dancing Script', cursive", fontSize: '32px' },
  { id: 'style2', name: 'Classic', fontFamily: "'Great Vibes', cursive", fontSize: '34px' },
  { id: 'style3', name: 'Modern', fontFamily: "'Pacifico', cursive", fontSize: '28px' },
  { id: 'style4', name: 'Formal', fontFamily: "'Allura', cursive", fontSize: '36px' },
  { id: 'style5', name: 'Bold', fontFamily: "'Sacramento', cursive", fontSize: '38px' },
];

// Terms that borrower must acknowledge for promissory notes
const PROMISSORY_TERMS = [
  "I have read and understand the full terms of this Promissory Note",
  "I agree to repay the specified amount by the due date",
  "I understand that failure to pay may result in legal action and additional fees",
  "I confirm that all information I have provided is accurate and truthful",
  "I acknowledge this is a legally binding agreement"
];

// Terms for credit repair contracts
const CREDIT_REPAIR_TERMS = [
  "I have read and understand all terms and conditions of this Credit Repair Service Agreement",
  "I agree to respond to all communications within 48 hours",
  "I will provide all requested documents in a timely manner",
  "I understand that failure to comply may result in forfeiture of all fees paid",
  "I acknowledge this is a legally binding agreement"
];

export default function SignContractPage() {
  const { contractId } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  
  // Terms acknowledgment
  const [checkedTerms, setCheckedTerms] = useState({});
  
  // ID Upload
  const [idFile, setIdFile] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  
  // Signature Date
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Signature Mode: 'draw' or 'type'
  const [signatureMode, setSignatureMode] = useState('draw');
  
  // Typed Signature
  const [typedName, setTypedName] = useState('');
  const [selectedSignatureStyle, setSelectedSignatureStyle] = useState('style1');
  
  // Signature Canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  useEffect(() => {
    // Initialize canvas
    if (canvasRef.current && contract && contract.status !== 'signed') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [contract]);

  const fetchContract = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/promise-to-pay/contract/${contractId}`);
      if (response.data.success) {
        setContract(response.data.contract);
        if (response.data.contract.status === 'signed') {
          setSigned(true);
        }
        
        // Track that the contract page was viewed
        try {
          await axios.post(`${API}/api/track/contract-view/${contractId}`);
        } catch (e) {
          // Silently fail - tracking is not critical
          console.log('View tracking failed:', e);
        }
        
        // Initialize checked terms state
        const terms = response.data.contract.contract_type === 'promissory_note' 
          ? PROMISSORY_TERMS 
          : CREDIT_REPAIR_TERMS;
        const initialChecked = {};
        terms.forEach((_, idx) => {
          initialChecked[idx] = false;
        });
        setCheckedTerms(initialChecked);
      }
    } catch (err) {
      console.error('Failed to fetch contract:', err);
      setError(err.response?.data?.detail || 'Contract not found');
    } finally {
      setLoading(false);
    }
  };

  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdFile({
          data: reader.result,
          name: file.name
        });
        setIdPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    let x, y;
    if (e.type === 'mousedown') {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    } else if (e.type === 'touchstart') {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    }
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    let x, y;
    if (e.type === 'mousemove') {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    } else if (e.type === 'touchmove') {
      e.preventDefault();
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    }
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (signatureMode === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    setHasSignature(false);
    setTypedName('');
  };

  // Generate typed signature as an image
  const generateTypedSignatureImage = (name, styleId) => {
    const style = SIGNATURE_STYLES.find(s => s.id === styleId) || SIGNATURE_STYLES[0];
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    
    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw signature text
    ctx.fillStyle = '#000000';
    ctx.font = `${style.fontSize} ${style.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, canvas.width / 2, canvas.height / 2);
    
    return canvas.toDataURL('image/png');
  };

  // Check if we have a valid signature
  const checkHasValidSignature = () => {
    if (signatureMode === 'draw') {
      return hasSignature;
    } else {
      return typedName.trim().length >= 2;
    }
  };

  const toggleTerm = (idx) => {
    setCheckedTerms(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const allTermsChecked = () => {
    const terms = contract?.contract_type === 'promissory_note' 
      ? PROMISSORY_TERMS 
      : CREDIT_REPAIR_TERMS;
    return terms.every((_, idx) => checkedTerms[idx]);
  };

  const handleSubmit = async () => {
    if (!allTermsChecked()) {
      toast.error('Please acknowledge all terms by checking each box');
      return;
    }
    if (!checkHasValidSignature()) {
      toast.error('Please sign the agreement');
      return;
    }
    if (!signatureDate) {
      toast.error('Please select the date of signature');
      return;
    }

    try {
      setIsSigning(true);
      
      // Get signature as base64 based on mode
      let signatureData;
      if (signatureMode === 'draw') {
        const canvas = canvasRef.current;
        signatureData = canvas.toDataURL('image/png');
      } else {
        signatureData = generateTypedSignatureImage(typedName, selectedSignatureStyle);
      }
      
      console.log('Submitting signature for contract:', contractId);
      
      const payload = {
        signature_data: signatureData,
        signature_date: signatureDate,
        signature_type: signatureMode // 'draw' or 'type'
      };
      
      // Include ID document if uploaded
      if (idFile && idFile.data) {
        payload.id_document = idFile.data;
        payload.id_document_name = idFile.name;
      }
      
      const response = await axios.post(`${API}/api/promise-to-pay/sign/${contractId}`, payload);

      console.log('Sign response:', response.data);

      if (response.data.success) {
        setSigned(true);
        toast.success('Contract signed successfully! Confirmation emails have been sent to both parties.');
      } else {
        // Handle unexpected response format
        console.error('Unexpected response:', response.data);
        toast.error(response.data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Failed to sign contract:', err);
      
      // Better error handling
      let errorMessage = 'Failed to sign contract. Please try again.';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSigning(false);
    }
  };

  const getContractTitle = () => {
    if (contract?.contract_type === 'promissory_note') {
      return 'Promissory Note';
    } else if (contract?.contract_type === 'credit_repair') {
      return 'Credit Repair Service Agreement';
    }
    return 'Agreement';
  };

  const getTermsList = () => {
    return contract?.contract_type === 'promissory_note' 
      ? PROMISSORY_TERMS 
      : CREDIT_REPAIR_TERMS;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading contract...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Contract Not Found</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (signed || contract?.status === 'signed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-8 max-w-md text-center">
          <Check className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Contract Signed!</h1>
          <p className="text-gray-400 mb-4">
            Thank you for signing the {getContractTitle()}. A copy has been sent to both parties.
          </p>
          <p className="text-sm text-gray-500">
            You may close this window.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`w-16 h-16 ${contract?.contract_type === 'promissory_note' ? 'bg-gradient-to-br from-purple-600 to-purple-700' : 'bg-gradient-to-br from-green-600 to-emerald-600'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">{getContractTitle()}</h1>
          <p className="text-gray-400 mt-2">Clear All Credit Repair</p>
          
          {/* Loan Number Badge */}
          {contract?.loan_number && (
            <div className="mt-4 inline-block bg-purple-900/30 border border-purple-500/30 rounded-lg px-6 py-3">
              <p className="text-purple-300 text-sm">Loan Reference Number</p>
              <p className="text-3xl font-bold text-purple-400"># {contract.loan_number}</p>
              <p className="text-purple-400/70 text-xs mt-1">Save this number for payment confirmations</p>
            </div>
          )}
        </div>

        {/* Lender Signature (for promissory notes) */}
        {contract?.contract_type === 'promissory_note' && contract?.lender_signature && (
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-purple-300 mb-3 flex items-center gap-2">
              <Pen className="w-5 h-5" />
              Lender's Signature
            </h2>
            <div className="bg-white rounded-lg p-2 inline-block">
              <img 
                src={contract.lender_signature} 
                alt="Lender Signature" 
                className="max-h-24"
              />
            </div>
            <p className="text-sm text-purple-300/70 mt-2">
              {contract.metadata?.lender_name || 'Lender'} has signed this agreement
            </p>
          </div>
        )}

        {/* Contract Content */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Agreement Details</h2>
          <div className="bg-gray-900 rounded-lg p-4 max-h-80 overflow-y-auto">
            <pre className="text-gray-300 text-sm whitespace-pre-wrap font-sans leading-relaxed">
              {contract?.contract_text}
            </pre>
          </div>
        </div>

        {/* Terms Acknowledgment */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-blue-400" />
            Acknowledge Terms
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            Please read and check each box to confirm you understand and agree to the terms:
          </p>
          
          <div className="space-y-3">
            {getTermsList().map((term, idx) => (
              <button
                key={idx}
                onClick={() => toggleTerm(idx)}
                className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                  checkedTerms[idx] 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center ${
                  checkedTerms[idx] ? 'bg-green-500 text-white' : 'bg-gray-700'
                }`}>
                  {checkedTerms[idx] && <Check className="w-4 h-4" />}
                </div>
                <span className={`text-sm ${checkedTerms[idx] ? 'text-green-300' : 'text-gray-300'}`}>
                  {term}
                </span>
              </button>
            ))}
          </div>
          
          {!allTermsChecked() && (
            <p className="text-amber-400 text-xs mt-3 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Please check all boxes above to continue
            </p>
          )}
        </div>

        {/* Signature */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Pen className="w-5 h-5 text-green-400" />
            Your Signature
          </h2>
          
          {/* Signature Mode Toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSignatureMode('draw')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                signatureMode === 'draw'
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <PenTool className="w-4 h-4" />
              Draw with Finger
            </button>
            <button
              onClick={() => setSignatureMode('type')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                signatureMode === 'type'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <Type className="w-4 h-4" />
              Type Your Name
            </button>
          </div>
          
          {/* Drawing Signature */}
          {signatureMode === 'draw' && (
            <>
              <p className="text-sm text-gray-400 mb-4">
                Sign in the box below using your mouse or finger (on touch devices).
              </p>
              
              <div className="bg-white rounded-lg overflow-hidden mb-3">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="w-full touch-none cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
            </>
          )}
          
          {/* Typed Signature */}
          {signatureMode === 'type' && (
            <>
              <p className="text-sm text-gray-400 mb-4">
                Type your full legal name below and select a signature style.
              </p>
              
              <div className="space-y-4">
                {/* Name Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Your Full Legal Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Type your full name here..."
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                  />
                </div>
                
                {/* Signature Style Selection */}
                {typedName.trim().length >= 2 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Choose Your Signature Style
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {SIGNATURE_STYLES.map((style) => (
                        <button
                          key={style.id}
                          onClick={() => setSelectedSignatureStyle(style.id)}
                          className={`p-4 bg-white rounded-lg border-2 transition-all ${
                            selectedSignatureStyle === style.id
                              ? 'border-blue-500 ring-2 ring-blue-500/30'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <p
                            className="text-black truncate"
                            style={{
                              fontFamily: style.fontFamily,
                              fontSize: style.fontSize,
                              lineHeight: '1.2'
                            }}
                          >
                            {typedName}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 font-sans">{style.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Preview */}
                {typedName.trim().length >= 2 && (
                  <div className="mt-4 p-4 bg-white rounded-lg">
                    <p className="text-xs text-gray-500 mb-2 font-sans">Signature Preview:</p>
                    <p
                      className="text-black text-center"
                      style={{
                        fontFamily: SIGNATURE_STYLES.find(s => s.id === selectedSignatureStyle)?.fontFamily,
                        fontSize: SIGNATURE_STYLES.find(s => s.id === selectedSignatureStyle)?.fontSize
                      }}
                    >
                      {typedName}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
          
          {/* Signature Date Picker */}
          <div className="mt-4 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Date of Signature <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              value={signatureDate}
              onChange={(e) => setSignatureDate(e.target.value)}
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-2">
              Select the date you are signing this document
            </p>
          </div>
          
          <div className="flex justify-between items-center mt-4">
            <p className="text-xs text-gray-500">
              Current Date/Time: {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short'
              })}
            </p>
            <button
              onClick={clearSignature}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Clear Signature
            </button>
          </div>
        </div>

        {/* Submit */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
          <p className="text-sm text-gray-400 mb-6">
            By signing below, I acknowledge that I have read and agree to the terms of this {getContractTitle()}. 
            I understand this is a legally binding document and my electronic signature is valid.
            <strong className="text-white block mt-2">A copy of the signed agreement will be sent to both parties.</strong>
          </p>
          
          <button
            onClick={handleSubmit}
            disabled={isSigning || !checkHasValidSignature() || !allTermsChecked() || !signatureDate}
            className={`w-full ${
              contract?.contract_type === 'promissory_note' 
                ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800' 
                : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
            } disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2`}
          >
            {isSigning ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Sign & Submit Agreement
              </>
            )}
          </button>
          
          {(!checkHasValidSignature() || !allTermsChecked()) && (
            <p className="text-center text-sm text-amber-400 mt-3">
              {!allTermsChecked() 
                ? 'Please acknowledge all terms above'
                : signatureMode === 'type' 
                  ? 'Please type your name above'
                  : 'Please sign above'}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-xs">
          <p>Clear All Credit Repair</p>
          <p className="mt-1">Your information is securely transmitted and stored.</p>
        </div>
      </div>
    </div>
  );
}
